import type * as api from '@gitbook/api-client';
import { Icon } from '@gitbook/spine-icons';
import { Card, confirm, DialogRoot, toast, useOverlayTriggerState } from '@gitbook/spine-react';
import { assertIsDefined } from '@gitbook/util-ts';

import { apiClient } from '../../../state';
import { SpaceCreationDialog, SpaceIcon } from '../../Space';
import { SpacePicker } from './SpacePickerField';
import { useAvailableSiteSpaces } from './useAvailableSiteSpaces';

export function SiteStructureEditSingleSpace(props: {
    organization: api.Organization;
    site: api.Site;
    siteStructure: api.SiteStructure;
    orgAllContent: api.OrganizationAllContent;
    siteSpace: api.SiteSpace;
}) {
    const { site, siteStructure, orgAllContent, siteSpace, organization } = props;
    const spaceId = siteSpace.space.id;
    const spaces = useAvailableSiteSpaces({ siteStructure, orgAllContent });
    const createSpaceDialogTrigger = useOverlayTriggerState();
    return (
        <>
            <DialogRoot {...createSpaceDialogTrigger.rootProps}>
                <SpaceCreationDialog organizationId={organization.id} />
            </DialogRoot>
            <Card className="p-4">
                <SpacePicker
                    orientation="horizontal"
                    spaces={[siteSpace.space, ...spaces]}
                    label="Site content"
                    description="Select which space power this site's content."
                    createSpaceDialogTrigger={createSpaceDialogTrigger}
                    value={spaceId}
                    onChange={(value) => {
                        const nextSpace = spaces.find((s) => s.id === value);
                        assertIsDefined(nextSpace, 'Space not found in available spaces');
                        confirm({
                            title: 'Update site content',
                            description: (
                                <>
                                    <p className="mb-4">
                                        Changing the space will replace all the content of your
                                        site.
                                    </p>
                                    <div className="flex items-center justify-between gap-8 text-base">
                                        <SpaceCard space={siteSpace.space} />
                                        <Icon.ArrowRight className="size-8" />
                                        <SpaceCard space={nextSpace} />
                                    </div>
                                </>
                            ),
                            confirmLabel: 'Update',
                            onConfirm: async () => {
                                await toast.catch(async () => {
                                    await apiClient.siteSpaces.updateSiteSpaceById(
                                        organization.id,
                                        site.id,
                                        siteSpace.id,
                                        { spaceId: value }
                                    );
                                    toast.success(`Site content updated to ${nextSpace.title}`);
                                }, 'Error while updating site space');
                            },
                        });
                    }}
                />
            </Card>
        </>
    );
}

function SpaceCard(props: { space: api.Space }) {
    const { space } = props;
    return (
        <div className="flex items-center gap-2 rounded-lg border p-4">
            <SpaceIcon space={space} />
            {space.title}
        </div>
    );
}
